import {
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { type ChangeEvent, useEffect, useRef, useState } from "react"
import {
  FiCheckCircle,
  FiMapPin,
  FiPhone,
  FiSettings,
  FiStar,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi"
import { fetchProtectedFileAsDataUrl } from "../../api/protectedFiles"
import {
  type CompanyProfileDraft,
  type CompanyProfileErrors,
  type CompanyProfileField,
  companyProfileFieldMaxLengths,
} from "./companyProfileForm"

type Props = {
  draft: CompanyProfileDraft
  errors: CompanyProfileErrors
  isGUSLoading: boolean
  canLookupGUS: boolean
  onChange: (field: CompanyProfileField, value: string) => void
  onLookupGUS: () => void
  labelLogoFile: File | null
  onLabelLogoFileChange: (file: File | null) => void
  onLabelLogoFileReadyChange: (ready: boolean) => void
  isLabelLogoFileRequired: boolean
}

type FieldProps = {
  field: CompanyProfileField
  label: string
  type?: string
  autoComplete?: string
  helper?: string
  isRequired?: boolean
  placeholder?: string
  inputMode?: "text" | "numeric" | "tel" | "email" | "url"
}

export default function CompanyProfileForm({
  draft,
  errors,
  isGUSLoading,
  canLookupGUS,
  onChange,
  onLookupGUS,
  labelLogoFile,
  onLabelLogoFileChange,
  onLabelLogoFileReadyChange,
  isLabelLogoFileRequired,
}: Props) {
  const panelBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200")
  const previewBg = useColorModeValue("gray.50", "whiteAlpha.100")

  const renderField = ({
    field,
    label,
    type = "text",
    autoComplete,
    helper,
    isRequired,
    placeholder,
    inputMode,
  }: FieldProps) => (
    <FormControl
      isRequired={isRequired}
      isInvalid={Boolean(errors[field])}
      size="sm"
    >
      <FormLabel htmlFor={`company-${field}`} fontSize="sm">
        {label}
      </FormLabel>
      <Input
        id={`company-${field}`}
        name={field}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        maxLength={companyProfileFieldMaxLengths[field]}
        value={draft[field]}
        onChange={(event) => onChange(field, event.target.value)}
        size="sm"
        borderRadius="xl"
      />
      {helper ? <FormHelperText fontSize="xs">{helper}</FormHelperText> : null}
      <FormErrorMessage fontSize="xs">{errors[field]}</FormErrorMessage>
    </FormControl>
  )

  return (
    <Stack spacing={5} minW={0}>
      <Section
        id="company-identity"
        icon={<FiStar />}
        title="Tożsamość firmy"
        description="Dane prawne i nazwy używane w systemie oraz na dokumentach."
        bg={panelBg}
        borderColor={borderColor}
      >
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField({
            field: "shortName",
            label: "Krótka nazwa",
            helper: "Zwięzła nazwa widoczna w interfejsie.",
            isRequired: true,
          })}
          {renderField({
            field: "legalName",
            label: "Nazwa prawna",
            helper: "Pełna nazwa firmy używana na dokumentach.",
            isRequired: true,
          })}
          {renderField({
            field: "wmsName",
            label: "Nazwa WMS",
            helper: "Nazwa produktu widoczna użytkownikom systemu.",
            isRequired: true,
          })}
          <FormControl isInvalid={Boolean(errors.taxId)} size="sm">
            <FormLabel htmlFor="company-taxId" fontSize="sm">
              NIP
            </FormLabel>
            <Stack spacing={2} direction={{ base: "column", sm: "row" }}>
              <Input
                id="company-taxId"
                name="taxId"
                inputMode="numeric"
                autoComplete="off"
                placeholder="np. 526-025-02-74"
                value={draft.taxId}
                onChange={(event) => onChange("taxId", event.target.value)}
                size="sm"
                borderRadius="xl"
              />
              <Button
                colorScheme="teal"
                variant="outline"
                size="sm"
                flexShrink={0}
                isDisabled={!canLookupGUS}
                isLoading={isGUSLoading}
                onClick={onLookupGUS}
              >
                Pobierz z GUS
              </Button>
            </Stack>
            <FormHelperText fontSize="xs">
              Dane z GUS pojawią się najpierw do porównania i nie zapiszą się
              automatycznie.
            </FormHelperText>
            <FormErrorMessage fontSize="xs">{errors.taxId}</FormErrorMessage>
          </FormControl>
          {renderField({
            field: "regon",
            label: "REGON",
            inputMode: "numeric",
          })}
          {renderField({ field: "krs", label: "KRS", inputMode: "numeric" })}
          {renderField({
            field: "bdo",
            label: "BDO",
            inputMode: "numeric",
            helper:
              "Numer rejestrowy BDO w Bazi Danych o Produktach i Opakowaniach.",
          })}
        </SimpleGrid>
      </Section>

      <Section
        id="company-contact"
        icon={<FiPhone />}
        title="Kontakt i adres"
        description="Dane kontaktowe i adres siedziby prezentowane na dokumentach."
        bg={panelBg}
        borderColor={borderColor}
      >
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {renderField({
            field: "email",
            label: "E-mail firmowy",
            type: "email",
            autoComplete: "email",
          })}
          {renderField({
            field: "phone",
            label: "Telefon",
            type: "tel",
            autoComplete: "tel",
          })}
          {renderField({
            field: "website",
            label: "Strona WWW",
            type: "url",
            autoComplete: "url",
            placeholder: "https://example.com",
          })}
          <Box display={{ base: "none", md: "block" }} />
          {renderField({
            field: "addressLine1",
            label: "Adres — wiersz 1",
            autoComplete: "street-address",
          })}
          {renderField({
            field: "addressLine2",
            label: "Adres — wiersz 2",
          })}
          {renderField({
            field: "postalCode",
            label: "Kod pocztowy",
            autoComplete: "postal-code",
          })}
          {renderField({
            field: "city",
            label: "Miasto",
            autoComplete: "address-level2",
          })}
        </SimpleGrid>
      </Section>

      <Section
        id="company-regional"
        icon={<FiMapPin />}
        title="Ustawienia regionalne"
        description="Domyślny język, kraj i strefa czasowa profilu firmy."
        bg={panelBg}
        borderColor={borderColor}
      >
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {renderField({
            field: "countryCode",
            label: "Kod kraju",
            isRequired: true,
            placeholder: "PL",
          })}
          <RegionalSelect
            id="company-locale"
            label="Język i format"
            value={draft.locale}
            currentValue={draft.locale}
            options={[{ value: "pl-PL", label: "Polski (Polska)" }]}
            error={errors.locale}
            onChange={(event) => onChange("locale", event.target.value)}
          />
          <RegionalSelect
            id="company-timezone"
            label="Strefa czasowa"
            value={draft.timezone}
            currentValue={draft.timezone}
            options={[
              { value: "Europe/Warsaw", label: "Europe/Warsaw (Polska)" },
            ]}
            error={errors.timezone}
            onChange={(event) => onChange("timezone", event.target.value)}
          />
        </SimpleGrid>
      </Section>

      <Section
        id="company-branding"
        icon={<FiSettings />}
        title="Branding"
        description="Oddzielne logo ekranu logowania i logo drukowane na etykietach."
        bg={panelBg}
        borderColor={borderColor}
      >
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
          <LogoEditor
            title="Logo ekranu logowania"
            purpose="Stały branding systemu Stockerra — nie podlega zmianie."
            url="/assets/images/stockerra_logo_full.png"
            alt="Stockerra WMS"
            previewBg={previewBg}
            borderColor={borderColor}
          />
          <PrintLogoEditor
            draft={draft}
            errors={errors}
            previewBg={previewBg}
            borderColor={borderColor}
            onChange={onChange}
            file={labelLogoFile}
            onFileChange={onLabelLogoFileChange}
            onFileReadyChange={onLabelLogoFileReadyChange}
            isFileRequired={isLabelLogoFileRequired}
          />
        </SimpleGrid>
      </Section>
    </Stack>
  )
}

function Section({
  id,
  icon,
  title,
  description,
  bg,
  borderColor,
  children,
}: {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  bg: string
  borderColor: string
  children: React.ReactNode
}) {
  return (
    <Box
      as="section"
      id={id}
      bg={bg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      p={{ base: 4, md: 5 }}
      scrollMarginTop="24px"
    >
      <HStack align="start" mb={4} spacing={3}>
        <Box color="teal.500" mt={1} aria-hidden="true">
          {icon}
        </Box>
        <Box>
          <Heading size="sm">{title}</Heading>
          <Text color="gray.500" mt={1} fontSize="xs">
            {description}
          </Text>
        </Box>
      </HStack>
      {children}
    </Box>
  )
}

function RegionalSelect({
  id,
  label,
  value,
  currentValue,
  options,
  error,
  onChange,
}: {
  id: string
  label: string
  value: string
  currentValue: string
  options: Array<{ value: string; label: string }>
  error?: string
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void
}) {
  const hasCurrent = options.some((option) => option.value === currentValue)
  return (
    <FormControl isRequired isInvalid={Boolean(error)} size="sm">
      <FormLabel htmlFor={id} fontSize="sm">
        {label}
      </FormLabel>
      <Select
        id={id}
        value={value}
        onChange={onChange}
        size="sm"
        borderRadius="xl"
      >
        {!hasCurrent && currentValue ? (
          <option value={currentValue}>{currentValue} (bieżąca wartość)</option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
      <FormErrorMessage fontSize="xs">{error}</FormErrorMessage>
    </FormControl>
  )
}

function LogoEditor({
  title,
  purpose,
  url,
  alt,
  previewBg,
  borderColor,
}: {
  title: string
  purpose: string
  url: string
  alt: string
  previewBg: string
  borderColor: string
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const previewFailed = Boolean(url && failedUrl === url)
  return (
    <Stack
      spacing={4}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      p={4}
    >
      <Box>
        <Heading size="sm">{title}</Heading>
        <Text color="gray.500" fontSize="sm" mt={1}>
          {purpose}
        </Text>
      </Box>
      <Box
        bg={previewBg}
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={borderColor}
        borderRadius="xl"
        minH="132px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        {url && !previewFailed ? (
          <Image
            src={url}
            alt={alt || title}
            maxH="100px"
            maxW="100%"
            objectFit="contain"
            referrerPolicy="no-referrer"
            onError={() => setFailedUrl(url)}
          />
        ) : (
          <Text
            color={previewFailed ? "red.500" : "gray.500"}
            fontSize="sm"
            textAlign="center"
          >
            {previewFailed
              ? "Nie udało się wyświetlić podglądu logo."
              : "Brak logo do podglądu"}
          </Text>
        )}
      </Box>
      <Text fontSize="sm" color="gray.500">
        Logo jest częścią identyfikacji produktu Stockerra i jest zarządzane
        przez system.
      </Text>
    </Stack>
  )
}

function PrintLogoEditor({
  draft,
  errors,
  previewBg,
  borderColor,
  onChange,
  file,
  onFileChange,
  onFileReadyChange,
  isFileRequired,
}: {
  draft: CompanyProfileDraft
  errors: CompanyProfileErrors
  previewBg: string
  borderColor: string
  onChange: (field: CompanyProfileField, value: string) => void
  file: File | null
  onFileChange: (file: File | null) => void
  onFileReadyChange: (ready: boolean) => void
  isFileRequired: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dropBg = useColorModeValue("teal.50", "whiteAlpha.100")
  const iconBoxBg = useColorModeValue("teal.100", "whiteAlpha.200")
  const [filePreview, setFilePreview] = useState<{
    file: File
    url: string
  } | null>(null)
  const [previewResult, setPreviewResult] = useState<{
    identity: string
    state: "local-ready" | "local-invalid" | "saved-ready" | "saved-unavailable"
  } | null>(null)
  const [persistedPreviewUrl, setPersistedPreviewUrl] = useState<string | null>(
    null,
  )
  const [retryNumber, setRetryNumber] = useState(0)
  const renderedPreviewUrl = useRef<string | null>(null)

  useEffect(() => {
    if (!file) {
      setFilePreview(null)
      onFileReadyChange(false)
      return
    }
    if (
      !["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
      file.size > 2 * 1024 * 1024
    ) {
      setFilePreview(null)
      onFileReadyChange(false)
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setFilePreview({ file, url: objectUrl })
    onFileReadyChange(false)
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [file, onFileReadyChange])

  useEffect(() => {
    let cancelled = false
    const source = draft.labelLogo
    if (file || draft.labelLogoSource !== "upload" || !source) {
      setPersistedPreviewUrl(file ? null : source || null)
      return () => {
        cancelled = true
      }
    }
    setPersistedPreviewUrl(null)
    if (source.startsWith("/files/")) {
      fetchProtectedFileAsDataUrl(source, "company-logo", {
        cache: retryNumber > 0 ? "reload" : "default",
      })
        .then((dataUrl) => {
          if (!cancelled) setPersistedPreviewUrl(dataUrl)
        })
        .catch(() => {
          if (!cancelled) setPersistedPreviewUrl(`error:${source}`)
        })
    } else {
      setPersistedPreviewUrl(source)
    }
    return () => {
      cancelled = true
    }
  }, [draft.labelLogo, draft.labelLogoSource, file, retryNumber])

  const filePreflightInvalid = Boolean(
    file &&
      (!["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
        file.size > 2 * 1024 * 1024),
  )
  const filePreviewUrl = filePreview?.file === file ? filePreview.url : null
  const previewUrl = file ? filePreviewUrl : persistedPreviewUrl
  renderedPreviewUrl.current = previewUrl
  const previewState = filePreflightInvalid
    ? "local-invalid"
    : previewUrl && previewResult?.identity === previewUrl
      ? previewResult.state
      : previewUrl || file
        ? "checking"
        : "idle"
  const fileError =
    file && !["image/png", "image/jpeg", "image/webp"].includes(file.type)
      ? "Nieobsługiwany format. Wybierz plik PNG, JPEG lub WebP."
      : file && file.size > 2 * 1024 * 1024
        ? "Plik jest zbyt duży. Maksymalny rozmiar to 2 MiB."
        : "Plik nie jest prawidłowym obrazem PNG, JPEG ani WebP i nie został przesłany."

  return (
    <Stack
      spacing={4}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      p={4}
    >
      <Box>
        <Heading size="sm">Logo etykiet i wydruków</Heading>
        <Text color="gray.500" fontSize="sm" mt={1}>
          Logo firmy używane na etykietach i wydrukach generowanych przez
          Stockerra.
        </Text>
      </Box>
      <Box
        bg={previewBg}
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={borderColor}
        borderRadius="xl"
        minH="132px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        {previewUrl &&
        previewState !== "local-invalid" &&
        previewState !== "saved-unavailable" ? (
          <Image
            key={previewUrl}
            src={previewUrl}
            alt={draft.labelLogoAlt || "Logo firmy"}
            maxH="100px"
            maxW="100%"
            objectFit="contain"
            referrerPolicy="no-referrer"
            onLoad={() => {
              if (renderedPreviewUrl.current !== previewUrl) return
              if (file) {
                setPreviewResult({
                  identity: previewUrl,
                  state: "local-ready",
                })
                onFileReadyChange(true)
                return
              }
              setPreviewResult({
                identity: previewUrl,
                state: "saved-ready",
              })
            }}
            onError={() => {
              if (renderedPreviewUrl.current !== previewUrl) return
              if (file) {
                setPreviewResult({
                  identity: previewUrl,
                  state: "local-invalid",
                })
                onFileReadyChange(false)
                return
              }
              setPreviewResult({
                identity: previewUrl,
                state: "saved-unavailable",
              })
            }}
          />
        ) : (
          <Text color="gray.500" fontSize="sm" textAlign="center">
            {previewState === "local-invalid"
              ? "Nie można wyświetlić wybranego pliku."
              : previewState === "saved-unavailable"
                ? "Zapisane logo jest chwilowo niedostępne."
                : file
                  ? "Sprawdzanie pliku…"
                  : "Domyślne logo Stockerra"}
          </Text>
        )}
      </Box>
      {file ? (
        <Box>
          <Text fontSize="sm" fontWeight="semibold">
            {file.name} · {(file.size / 1024).toFixed(1)} KiB ·{" "}
            {file.type || "nieznany format"}
          </Text>
          {previewState === "checking" ? (
            <Text fontSize="sm" color="blue.500">
              Sprawdzanie, czy obraz można wyświetlić…
            </Text>
          ) : null}
          {previewState === "local-ready" ? (
            <Text fontSize="sm" color="green.500">
              Gotowe do zapisania. Podgląd jest lokalny — użyj „Zapisz zmiany”,
              aby przesłać logo.
            </Text>
          ) : null}
          {previewState === "local-invalid" ? (
            <Text fontSize="sm" color="red.500">
              {fileError}
            </Text>
          ) : null}
        </Box>
      ) : null}
      {!file &&
      draft.labelLogoSource === "upload" &&
      previewState === "checking" ? (
        <Text fontSize="sm" color="blue.500">
          Sprawdzanie dostępności zapisanego logo…
        </Text>
      ) : null}
      {!file &&
      draft.labelLogoSource === "upload" &&
      previewState === "saved-ready" ? (
        <Text fontSize="sm" color="green.500">
          Logo zapisano i jest dostępne dla etykiet oraz wydruków.
        </Text>
      ) : null}
      {!file &&
      draft.labelLogoSource === "upload" &&
      previewState === "saved-unavailable" ? (
        <Box>
          <Text fontSize="sm" color="red.500">
            Logo zostało zapisane, ale obecnie nie można go pobrać z serwera.
          </Text>
          <Button
            mt={2}
            size="sm"
            variant="outline"
            onClick={() => {
              setRetryNumber((value) => value + 1)
            }}
          >
            Spróbuj ponownie pobrać
          </Button>
        </Box>
      ) : null}
      <FormControl size="sm">
        <FormLabel fontSize="sm">Źródło logo</FormLabel>
        <RadioGroup
          size="sm"
          value={draft.labelLogoSource}
          onChange={(value) => {
            if (value !== "upload") onFileChange(null)
            onChange("labelLogoSource", value)
          }}
        >
          <Stack direction={{ base: "column", sm: "row" }} spacing={4}>
            <Radio value="upload" size="sm">
              Prześlij plik
            </Radio>
            <Radio value="url" size="sm">
              Adres URL
            </Radio>
            <Radio value="default" size="sm">
              Domyślne Stockerra
            </Radio>
            {draft.labelLogoSource === "legacy" ? (
              <Radio value="legacy" size="sm" isDisabled>
                Istniejąca ścieżka lokalna
              </Radio>
            ) : null}
          </Stack>
        </RadioGroup>
        {draft.labelLogoSource === "legacy" ? (
          <FormHelperText fontSize="xs">
            Zachowano istniejącą lokalną ścieżkę logo. Wybierz plik, HTTPS URL
            albo logo domyślne, aby ją zastąpić.
          </FormHelperText>
        ) : null}
      </FormControl>
      {draft.labelLogoSource === "upload" ? (
        <FormControl isInvalid={isFileRequired}>
          <FormLabel htmlFor="company-labelLogoFile">Plik logo</FormLabel>
          <Input
            ref={fileInputRef}
            id="company-labelLogoFile"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            display="none"
            onChange={(event) => {
              onFileChange(event.target.files?.[0] ?? null)
            }}
          />
          {file ? (
            <Box
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="xl"
              p={4}
              bg={previewBg}
            >
              <HStack justify="space-between" align="center" spacing={3}>
                <HStack spacing={3} minW={0} flex={1}>
                  <Box color="teal.500" fontSize="xl" flexShrink={0}>
                    <FiCheckCircle />
                  </Box>
                  <Box minW={0} flex={1}>
                    <Text fontSize="sm" fontWeight="600" isTruncated>
                      {file.name}
                    </Text>
                    <HStack spacing={2} mt={0.5}>
                      <Badge colorScheme="teal" variant="subtle" fontSize="xs">
                        {(file.size / 1024).toFixed(1)} KiB
                      </Badge>
                      <Text fontSize="xs" color="gray.500">
                        {file.type || "Obraz"}
                      </Text>
                    </HStack>
                  </Box>
                </HStack>
                <HStack spacing={2} flexShrink={0}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="teal"
                    borderRadius="lg"
                    leftIcon={<FiUploadCloud />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Zmień
                  </Button>
                  <IconButton
                    aria-label="Usuń wybrany plik"
                    icon={<FiTrash2 />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    borderRadius="lg"
                    onClick={() => onFileChange(null)}
                  />
                </HStack>
              </HStack>
            </Box>
          ) : (
            <Box
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={isDragging ? "teal.500" : borderColor}
              borderRadius="xl"
              p={6}
              bg={isDragging ? dropBg : previewBg}
              _hover={{ borderColor: "teal.500", bg: dropBg }}
              cursor="pointer"
              transition="all 0.2s ease"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(true)
              }}
              onDragLeave={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(false)
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(false)
                const droppedFile = e.dataTransfer.files?.[0]
                if (droppedFile) {
                  onFileChange(droppedFile)
                }
              }}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
            >
              <Box
                p={3}
                borderRadius="full"
                bg={iconBoxBg}
                color="teal.500"
                mb={3}
              >
                <FiUploadCloud size={24} />
              </Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                Przeciągnij i upuść plik logo lub{" "}
                <Text as="span" color="teal.500" textDecoration="underline">
                  wybierz z dysku
                </Text>
              </Text>
              <Text fontSize="xs" color="gray.500">
                PNG (zalecany), JPEG lub WebP. Maksymalnie 2 MiB.
              </Text>
            </Box>
          )}
          {isFileRequired ? (
            <FormErrorMessage mt={2}>
              Wybierz prawidłowy plik logo przed zapisaniem zmian.
            </FormErrorMessage>
          ) : null}
        </FormControl>
      ) : null}
      {draft.labelLogoSource === "url" ? (
        <FormControl isInvalid={Boolean(errors.labelLogo)}>
          <FormLabel htmlFor="company-labelLogo">
            Bezpośredni adres obrazu
          </FormLabel>
          <Input
            id="company-labelLogo"
            type="url"
            value={draft.labelLogo}
            onChange={(event) => {
              onChange("labelLogo", event.target.value)
            }}
            placeholder="https://example.com/logo.png"
            maxLength={companyProfileFieldMaxLengths.labelLogo}
            borderRadius="xl"
          />
          <FormHelperText>
            Publiczny adres HTTPS musi zwracać obraz i zezwalać na użycie w
            przeglądarce (CORS).
          </FormHelperText>
          <FormErrorMessage>{errors.labelLogo}</FormErrorMessage>
        </FormControl>
      ) : null}
      <FormControl>
        <FormLabel htmlFor="company-labelLogoAlt">Tekst alternatywny</FormLabel>
        <Input
          id="company-labelLogoAlt"
          value={draft.labelLogoAlt}
          onChange={(event) => onChange("labelLogoAlt", event.target.value)}
          placeholder="np. Logo firmy"
          maxLength={companyProfileFieldMaxLengths.labelLogoAlt}
          borderRadius="xl"
        />
      </FormControl>
      <Box bg={previewBg} borderRadius="lg" p={3}>
        <Text fontSize="sm" fontWeight="semibold">
          Zalecane parametry
        </Text>
        <Text fontSize="sm" color="gray.500" mt={1}>
          PNG z przezroczystym tłem, proporcje 3:1, zalecane 1200×400 px,
          minimum 600×200 px, maksymalnie 2 MiB. Zachowaj wolną przestrzeń wokół
          znaku.
        </Text>
      </Box>
    </Stack>
  )
}
